import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import BaseSticky from 'react-stickynode';
import styled, { css } from 'styled-components';

import {
  desktop,
  mobile,
  useBreakpoint,
  useProductBrowser,
} from '@techstyle/react-components';

import {
  useIntersectionObserver,
  useIsomorphicLayoutEffect,
} from '../../utils/use-hooks';
import { useHeaderStatus } from '../../utils/useHeaderStatus';
import { usePageNum } from '../GridPageNumProvider/GridPageNumProvider';
import {
  ProductGridMobileFloatingFilterButton,
  ProductGridSortWrapper,
} from '../ProductGrid';
import { ProductGridCount } from '../ProductGridCount';
import ProductSortDropdown from '../ProductSortDropdown';

import * as GridFilterBar from './GridFilterBar';
import type { PartialCatalogProductFilters, TitleProps } from './GridFilterBar';
import { supportedAggregations } from './GridFilterBar.builder';

type StickyProps = {
  $transitionDurationEnabled: boolean;
  $delayEnabled: boolean;
};

type CollapsedProp = {
  $collapsed: boolean;
  filterItems?: GridFilterBar.FilterItem[];
};

const Wrapper = styled.div`
  margin-bottom: 8px;
`;

const Sticky = styled(BaseSticky)<StickyProps>`
  & > .sticky-inner-wrapper {
    z-index: 2;
    background-color: #fff;
    ${({ $transitionDurationEnabled }) =>
      $transitionDurationEnabled &&
      css`
        transition-property: transform;
        transition-duration: 500ms;
      `}
    ${({ $delayEnabled }) =>
      $delayEnabled &&
      css`
        transition-delay: 750ms;
      `};

    z-index: ${({ theme }) => theme.zIndex.productFilterBar};
  }
`;

const CarouselWrapper = styled.div<CollapsedProp>`
  ${mobile`
    padding: 0 16px;
  `}

  ${desktop`
    ${({ $collapsed }) =>
      !$collapsed &&
      css`
        margin-bottom: 24px;
      `}
  `}

  ${({ filterItems }) => !filterItems?.length && 'display: none;'}
`;

const FilterBarWrapper = styled.div<CollapsedProp>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${({ $collapsed, theme }) =>
    $collapsed &&
    css`
      box-shadow: 0 6px 4px -4px ${theme.colors.lightShadow1};
    `}

  ${desktop`
    ${({ $collapsed, theme }) =>
      $collapsed &&
      css`
        padding-bottom: ${theme.spacing.tiny}px;
      `}

    gap: 16px;
  `}


  ${mobile`
    ${({ $collapsed, filterItems, theme }) =>
      $collapsed
        ? css`
            padding-top: ${filterItems?.length ? theme.spacing.small : 0}px;
          `
        : css`
            padding-top: ${theme.spacing.small}px;
          `}
  `}
`;

const FilterBarTopSection = styled.div<CollapsedProp>`
  display: grid;
  grid-template-columns: 1fr max-content;
  column-gap: 8px;

  ${mobile`
    padding: 0 16px;
    ${({ $collapsed }) =>
      $collapsed &&
      css`
        display: none;
      `}
  `}

  ${desktop`
    grid-template-columns: auto minmax(max-content, 1fr) 1fr;
    column-gap: 16px;
    
    ${({ $collapsed }) =>
      !$collapsed &&
      css`
        margin-top: -24px;
      `}
  `}
`;

const Arrows = styled(GridFilterBar.Arrows)`
  ${mobile`
    display: none;
  `}
  align-items: flex-end;
  justify-content: flex-end;
`;

type AggregationFilterOption = typeof supportedAggregations[number];

type BuilderGridFilterBarAggregationFilter = {
  filter: {
    filterType: AggregationFilterOption;
    filterValue: string;
  };
};

type BuilderGridFilterBarFilterOptions = {
  label: string;
  image?: string;
  trackingKey?: string;
  filterOptions?: BuilderGridFilterBarAggregationFilter[];
};

type BuilderGridFilterBarProps = TitleProps & {
  filterItems: BuilderGridFilterBarFilterOptions[];
  builderState: {
    context: { buttonContainerRef: HTMLDivElement };
    state: {
      buttonContainerRef: HTMLDivElement;
      showMobileFilterButton: boolean;
    };
  };
};

const convertFilterOption = (
  filterOptions: BuilderGridFilterBarAggregationFilter[]
) => {
  if (!filterOptions) {
    return null;
  }
  const newFilter = {} as PartialCatalogProductFilters;

  filterOptions.forEach(({ filter }) => {
    if (filter) {
      const { filterType, filterValue = '' } = filter;
      const filterList = filterValue.split(',').map(value => value.trim());

      if (supportedAggregations.includes(filterType)) {
        newFilter.aggregationFilter = {
          ...newFilter.aggregationFilter,
          [filter.filterType]: filterList,
        };
        newFilter.type = filterType;
      }
    }
  });

  return newFilter;
};

const BuilderGridFilterBar = ({
  defaultTitle,
  shortTitle,
  filterItems: builderFilterItems,
  builderState: {
    context: { buttonContainerRef: contextButtonContainerRef },
    state: {
      buttonContainerRef: stateButtonContainerRef,
      showMobileFilterButton = true,
    },
  },
}: BuilderGridFilterBarProps) => {
  const buttonContainerRef =
    contextButtonContainerRef || stateButtonContainerRef || null;
  const { headerHeight, hasAutoHide } = useHeaderStatus();
  const headerHeightRef = useRef<number>(headerHeight);
  const [carouselInitialized, setCarouselInitialized] = useState(false);
  const stickyWrapperRef = useRef<HTMLElement | null>(null);
  // @ts-ignore -- Inferred type for `useBreakpoint` is incorrect
  const { isMobile } = useBreakpoint();
  const [setIntersectionRef, intersectionEntry] = useIntersectionObserver({
    threshold: isMobile ? 0.5 : 1,
  });
  const { pageNum } = usePageNum();
  const filterBarWrapperRef = useRef<HTMLDivElement>(null);
  const carouselFullHeightRef = useRef<number>(0);
  const { sort: productBrowserSort, filters: productBrowserFilters } =
    useProductBrowser();

  const filterItems = useMemo(() => {
    if (!builderFilterItems || !builderFilterItems.length) {
      return [];
    }

    return builderFilterItems.map(({ filterOptions, label, ...rest }) => ({
      ...rest,
      label,
      id: `${filterOptions.map(opt => opt?.filter?.filterValue).toString()}`,
      filter: convertFilterOption(filterOptions),
    }));
  }, [builderFilterItems]);

  /**
   * The Sticky component sets a height on the outer wrapper based on the height
   * of its content. This is a problem for us when the carousel needs to change to
   * the collapsed state and become sticky at the same time -- when the height of the
   * carousel changes, the scroll position will jump based on the added/removed height.
   * To avoid this we need to set the height of the sticky container to be the same as
   * the carousel when it's in the full-size state before it collapses.
   * */
  const carouselCollapsed = useMemo(() => {
    // We don't want to collapse the carousel if it hasn't been initialized yet since
    // we won't know the proper height to set the sticky container to.
    if (!carouselInitialized || !intersectionEntry) {
      return false;
    }

    const { boundingClientRect, isIntersecting } = intersectionEntry;

    const collapsed = boundingClientRect.top >= 0 ? false : !isIntersecting;

    // NOTE: Setting this stuff up here is a bit hacky, but it's the simplest way to
    // set up the heights properly before the carousel collapses itself.
    if (collapsed && !carouselFullHeightRef.current) {
      // Snapshot the height of the carousel before it collapses
      const { height: carouselHeight } =
        filterBarWrapperRef.current.getBoundingClientRect();
      carouselFullHeightRef.current = carouselHeight;
      stickyWrapperRef.current.style.minHeight = `${carouselFullHeightRef.current}px`;
    } else if (!collapsed) {
      stickyWrapperRef.current.style.minHeight = null;
      carouselFullHeightRef.current = 0;
    }

    return collapsed;
  }, [carouselInitialized, intersectionEntry]);
  const carouselCollapsedRef = useRef(carouselCollapsed);

  const onCarouselInit = useCallback(() => {
    setCarouselInitialized(true);
  }, []);

  const carouselOptions = useMemo(() => {
    return {
      onInit: onCarouselInit,
    };
  }, [onCarouselInit]);

  // We need to set up the intersectionRef and the stickyWrapperRef based on the
  // same element, so we use a callback ref to set both at the same time.
  const setupIntersectionRef = useCallback(
    (node: HTMLElement | null) => {
      stickyWrapperRef.current = node;
      setIntersectionRef(node);
    },
    [setIntersectionRef]
  );

  // Keep track of the header height in a ref so we don't trigger the scroll into view `useLayoutEffect`
  useEffect(() => {
    carouselCollapsedRef.current = carouselCollapsed;
    headerHeightRef.current = headerHeight;
  }, [carouselCollapsed, headerHeight]);

  // NOTE: This isn't super ideal, it'd be a lot better if we can trigger this with an event after
  // the filters/sort has been updated, but for now this is the best we can do.
  useIsomorphicLayoutEffect(() => {
    if (carouselCollapsedRef.current && carouselInitialized) {
      // Hard code mobile height difference for now
      let heightDifference = 90;

      if (!isMobile) {
        heightDifference = -(
          carouselFullHeightRef.current -
          filterBarWrapperRef.current.getBoundingClientRect().height -
          headerHeightRef.current
        );
      }

      stickyWrapperRef.current.style.scrollMarginTop = `${heightDifference}px`;
      stickyWrapperRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [
    carouselCollapsed,
    carouselInitialized,
    productBrowserSort,
    productBrowserFilters,
    isMobile,
    pageNum,
  ]);

  return (
    <GridFilterBar.Root
      carouselCollapsed={carouselCollapsed}
      filterItems={filterItems}
    >
      <Wrapper ref={setupIntersectionRef}>
        <Sticky
          top={headerHeight}
          enabled={carouselCollapsed}
          $transitionDurationEnabled={carouselCollapsed}
          $delayEnabled={hasAutoHide}
        >
          <FilterBarWrapper
            $collapsed={carouselCollapsed}
            filterItems={filterItems}
            ref={filterBarWrapperRef}
          >
            <FilterBarTopSection $collapsed={carouselCollapsed}>
              <GridFilterBar.Heading
                shortTitle={shortTitle}
                defaultTitle={defaultTitle}
              />
              <GridFilterBar.ClearButton />
              <Arrows />
            </FilterBarTopSection>
            <CarouselWrapper
              $collapsed={carouselCollapsed}
              filterItems={filterItems}
            >
              <GridFilterBar.Carousel carouselOptions={carouselOptions} />
            </CarouselWrapper>
            {!isMobile && !carouselCollapsed && (
              <ProductGridSortWrapper>
                <ProductGridCount />
                <ProductSortDropdown />
              </ProductGridSortWrapper>
            )}
          </FilterBarWrapper>
        </Sticky>
      </Wrapper>
      {isMobile && (
        <ProductGridSortWrapper>
          <ProductGridCount isMobileAndVariant />
          {showMobileFilterButton && (
            <ProductGridMobileFloatingFilterButton
              filterBarEnabled
              isBottom={isMobile}
              buttonContainerRef={buttonContainerRef}
            />
          )}
        </ProductGridSortWrapper>
      )}
    </GridFilterBar.Root>
  );
};

export default BuilderGridFilterBar;
